import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Client, Contact, Interaction, PipelineStage, ClientType } from '@/types/clients';

// ─────────── Clients ───────────

export type ClientFilters = {
  stage?: PipelineStage;
  type?: ClientType;
  ownerUid?: string;
  search?: string;
  limit?: number;
};

export function useClients(filters: ClientFilters = {}) {
  return useQuery({
    queryKey: ['clients', filters],
    queryFn: async () => {
      const { data } = await api.get<{ items: Client[] }>('/clients', { params: filters });
      return data.items;
    },
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id],
    queryFn: async () => {
      const { data } = await api.get<Client>(`/clients/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export type SaveClientInput = Omit<Client, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy' | 'lastInteractionAt'>;

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveClientInput) => {
      const { data } = await api.post<{ id: string }>('/clients', input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<SaveClientInput>) => {
      const { data } = await api.patch(`/clients/${id}`, patch);
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client', vars.id] });
    },
  });
}

export function useUpdateClientStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, stage }: { id: string; stage: PipelineStage }) => {
      const { data } = await api.patch(`/clients/${id}/stage`, { stage });
      return data;
    },
    onMutate: async ({ id, stage }) => {
      // Optimistic update for the pipeline drag
      await qc.cancelQueries({ queryKey: ['clients'] });
      const previous = qc.getQueriesData<Client[]>({ queryKey: ['clients'] });
      qc.setQueriesData<Client[]>({ queryKey: ['clients'] }, (old) =>
        old?.map((c) => (c.id === id ? { ...c, stage } : c)) ?? old,
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.previous.forEach(([key, val]) => qc.setQueryData(key, val));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/clients/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

// ─────────── Contacts ───────────

export function useContacts(clientId: string | undefined) {
  return useQuery({
    queryKey: ['contacts', clientId],
    queryFn: async () => {
      const { data } = await api.get<{ items: Contact[] }>(`/clients/${clientId}/contacts`);
      return data.items;
    },
    enabled: !!clientId,
  });
}

export type SaveContactInput = Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>;

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveContactInput) => {
      const { data } = await api.post(`/clients/${input.clientId}/contacts`, input);
      return data;
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['contacts', vars.clientId] }),
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, clientId, ...patch }: { id: string; clientId: string } & Partial<SaveContactInput>) => {
      const { data } = await api.patch(`/clients/${clientId}/contacts/${id}`, patch);
      return data;
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['contacts', vars.clientId] }),
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, clientId }: { id: string; clientId: string }) => {
      const { data } = await api.delete(`/clients/${clientId}/contacts/${id}`);
      return data;
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['contacts', vars.clientId] }),
  });
}

// ─────────── Interactions ───────────

export function useInteractions(clientId: string | undefined) {
  return useQuery({
    queryKey: ['interactions', clientId],
    queryFn: async () => {
      const { data } = await api.get<{ items: Interaction[] }>(`/clients/${clientId}/interactions`);
      return data.items;
    },
    enabled: !!clientId,
  });
}

export type LogInteractionInput = Omit<Interaction, 'id' | 'authorUid' | 'authorName' | 'createdAt'>;

export function useLogInteraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LogInteractionInput) => {
      const { data } = await api.post(`/clients/${input.clientId}/interactions`, input);
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['interactions', vars.clientId] });
      qc.invalidateQueries({ queryKey: ['client', vars.clientId] });
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}
